/**
 * Pure Shrink-to-fit-Berechnung für Textfelder.
 * Entscheidet NUR die Schriftgröße; das tatsächliche Zeilen-Wrapping macht
 * jeder Renderer nativ (Browser: CSS, jsPDF: splitTextToSize).
 * Breitenmessung ist eine Heuristik (widthFactor ≈ mittlere Zeichenbreite/fontSize),
 * bewusst identisch in src/lib und infra/lambda/shared (wie gridLayout dupliziert).
 */
export interface FitTextInput {
  text: string;
  baseFontSize: number;
  cellWidth: number;
  cellHeight: number;
  widthFactor: number;
  bold: boolean;
  minFontSize?: number;
  lineHeight?: number;
}

export function fitTextToCell(input: FitTextInput): number {
  const min = input.minFontSize ?? 6;
  const lineHeight = input.lineHeight ?? 1.3;
  const factor = input.widthFactor * (input.bold ? 1.05 : 1);
  const paragraphs = input.text.split('\n');

  for (let size = Math.round(input.baseFontSize); size >= min; size--) {
    const charsPerLine = Math.max(1, Math.floor(input.cellWidth / (size * factor)));
    let lines = 0;
    for (const p of paragraphs) {
      // Wortweises Wrapping; überbreite Wörter werden hart umbrochen
      let current = 0;
      lines++;
      for (const word of p.split(/\s+/).filter(Boolean)) {
        const len = word.length;
        if (current === 0) {
          if (len <= charsPerLine) { current = len; }
          else { lines += Math.ceil(len / charsPerLine) - 1; current = len % charsPerLine || charsPerLine; }
        } else if (current + 1 + len <= charsPerLine) {
          current += 1 + len;
        } else if (len <= charsPerLine) {
          lines++; current = len;
        } else {
          lines += Math.ceil(len / charsPerLine); current = len % charsPerLine || charsPerLine;
        }
      }
    }
    if (lines * size * lineHeight <= input.cellHeight) return size;
  }
  return min;
}

/**
 * Wortweiser Zeilenumbruch mit Index-Tracking (für Farb-Runs im PDF).
 * Liefert Zeilen als [start, end)-Fenster auf text; \n und die Leerzeichen,
 * an denen umbrochen wurde, gehören zu keiner Zeile. measure liefert die
 * Renderbreite eines Strings (z.B. jsPDF getTextWidth).
 */
export function wrapTextWithIndices(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
): { start: number; end: number }[] {
  const lines: { start: number; end: number }[] = [];

  const wrapParagraph = (start: number, end: number) => {
    if (start >= end) { lines.push({ start, end: start }); return; }
    let lineStart = start;
    let i = start;
    let lastBreak = -1; // Zeilenposition NACH dem letzten umbruchfähigen Leerzeichen
    while (i < end) {
      let wordEnd = i;
      while (wordEnd < end && text[wordEnd] !== ' ') wordEnd++;
      if (measure(text.slice(lineStart, wordEnd)) > maxWidth) {
        if (lastBreak > lineStart) {
          lines.push({ start: lineStart, end: lastBreak - 1 });
          lineStart = lastBreak;
          lastBreak = -1;
          i = lineStart;
          continue;
        }
        // überlanges einzelnes Wort: hart zeichenweise brechen
        let cut = lineStart + 1;
        while (cut < wordEnd && measure(text.slice(lineStart, cut + 1)) <= maxWidth) cut++;
        lines.push({ start: lineStart, end: cut });
        lineStart = cut;
        lastBreak = -1;
        i = cut;
        continue;
      }
      i = wordEnd < end ? wordEnd + 1 : wordEnd;
      if (wordEnd < end) lastBreak = wordEnd + 1;
    }
    lines.push({ start: lineStart, end });
  };

  let parStart = 0;
  let nl = text.indexOf('\n');
  while (nl !== -1) {
    wrapParagraph(parStart, nl);
    parStart = nl + 1;
    nl = text.indexOf('\n', parStart);
  }
  wrapParagraph(parStart, text.length);
  return lines;
}
